import {
  BoxGeometry,
  DoubleSide,
  Mesh,
  MeshBasicMaterial,
  type CanvasTexture,
  type Scene,
} from 'three';
import {
  BLOCK_HARDNESS,
  BLOCK_MINE_POWER,
  ITEM_CATALOG,
  ItemId,
  type ItemDef,
} from './catalog';
import { BlockType, isMineable, type Vector3D } from '../types';
import {
  CRACK_STAGE_COUNT,
  crackStageFromFraction,
  createCrackStageTextures,
} from '../world/crackTextures';
import { raycastVoxels } from '../world/raycast';
import type { WorldManager } from '../world/WorldManager';

export interface MiningTarget {
  x: number;
  y: number;
  z: number;
  blockType: BlockType;
  progress: number;
  hardness: number;
  fraction: number;
}

interface DamageEntry {
  progress: number;
  blockType: BlockType;
  mesh: Mesh;
  material: MeshBasicMaterial;
  stage: number;
}

const KEY = (x: number, y: number, z: number): string => `${x},${y},${z}`;
const MAX_CRACK_MESHES = 64;

/**
 * Hold-to-mine with persistent crack overlays.
 * Damage (and visible cracks) stay on the block until it breaks or the world regenerates.
 */
export class MiningSystem {
  private readonly world: WorldManager;
  private readonly scene: Scene;
  private readonly damage = new Map<string, DamageEntry>();
  private readonly crackTextures: CanvasTexture[];
  private readonly sharedGeo: BoxGeometry;

  private mining = false;
  private current: MiningTarget | null = null;
  private swingTimer = 0;

  constructor(scene: Scene, world: WorldManager) {
    this.scene = scene;
    this.world = world;
    this.crackTextures = createCrackStageTextures();
    this.sharedGeo = new BoxGeometry(1.015, 1.015, 1.015);
  }

  get target(): MiningTarget | null {
    return this.current;
  }

  get isMining(): boolean {
    return this.mining;
  }

  setMining(active: boolean): void {
    this.mining = active;
    if (!active) {
      // Keep cracks / damage — only stop applying new work.
      this.current = null;
      this.swingTimer = 0;
    } else {
      this.swingTimer = 0;
    }
  }

  update(
    dt: number,
    eye: Vector3D,
    direction: Readonly<Vector3D>,
    itemId: ItemId | null,
    onSwing: () => void,
    onBreak: (x: number, y: number, z: number, type: BlockType) => void,
    onDenied: (type: BlockType, requiredPower: number) => void,
  ): boolean {
    // Prune overlays if the underlying block changed (mined elsewhere / regen).
    this.validateDamageEntries();

    if (!this.mining || !itemId) {
      this.current = null;
      return false;
    }

    const def = ITEM_CATALOG[itemId];
    if (def.category !== 'tool' || def.minePower <= 0) {
      this.current = null;
      return false;
    }

    const reach = def.reach > 0 ? def.reach : 5;
    const dir = { x: direction.x, y: direction.y, z: direction.z };
    const hit = raycastVoxels(this.world, eye, dir, reach);
    if (!hit || !isMineable(hit.blockType)) {
      this.current = null;
      return false;
    }

    const required = BLOCK_MINE_POWER[hit.blockType] ?? 99;
    if (def.minePower < required) {
      this.current = null;
      onDenied(hit.blockType, required);
      return false;
    }

    const hardness = BLOCK_HARDNESS[hit.blockType] ?? 3;
    const key = KEY(hit.x, hit.y, hit.z);
    let entry = this.damage.get(key);
    if (!entry || entry.blockType !== hit.blockType) {
      if (entry) this.destroyEntry(key);
      entry = this.createEntry(hit.x, hit.y, hit.z, hit.blockType);
      this.damage.set(key, entry);
      this.evictOldestIfNeeded();
    }

    const speed = mineSpeedOf(def);
    entry.progress += speed * dt;

    const fraction = Math.min(1, entry.progress / hardness);
    this.syncCrackVisual(entry, hit.x, hit.y, hit.z, fraction);

    this.current = {
      x: hit.x,
      y: hit.y,
      z: hit.z,
      blockType: hit.blockType,
      progress: entry.progress,
      hardness,
      fraction,
    };

    this.swingTimer += dt;
    const swingInterval = Math.max(0.12, 0.38 / Math.sqrt(speed));
    if (this.swingTimer >= swingInterval) {
      this.swingTimer = 0;
      onSwing();
    }

    if (entry.progress >= hardness) {
      this.destroyEntry(key);
      this.current = null;
      onBreak(hit.x, hit.y, hit.z, hit.blockType);
      return true;
    }

    return false;
  }

  clearAt(x: number, y: number, z: number): void {
    this.destroyEntry(KEY(x, y, z));
    if (this.current && this.current.x === x && this.current.y === y && this.current.z === z) {
      this.current = null;
    }
  }

  clearAll(): void {
    for (const key of [...this.damage.keys()]) {
      this.destroyEntry(key);
    }
    this.current = null;
  }

  dispose(): void {
    this.clearAll();
    this.sharedGeo.dispose();
    for (const t of this.crackTextures) t.dispose();
  }

  private createEntry(x: number, y: number, z: number, blockType: BlockType): DamageEntry {
    const material = new MeshBasicMaterial({
      map: this.crackTextures[0]!,
      transparent: true,
      opacity: 1,
      depthWrite: false,
      side: DoubleSide,
      alphaTest: 0.05,
      polygonOffset: true,
      polygonOffsetFactor: -2,
      polygonOffsetUnits: -2,
    });
    const mesh = new Mesh(this.sharedGeo, material);
    mesh.renderOrder = 10;
    const s = this.world.blockSize;
    mesh.position.set((x + 0.5) * s, (y + 0.5) * s, (z + 0.5) * s);
    mesh.scale.setScalar(s);
    mesh.visible = false;
    this.scene.add(mesh);

    return {
      progress: 0,
      blockType,
      mesh,
      material,
      stage: -1,
    };
  }

  private syncCrackVisual(
    entry: DamageEntry,
    x: number,
    y: number,
    z: number,
    fraction: number,
  ): void {
    const stage = crackStageFromFraction(fraction);
    const s = this.world.blockSize;
    entry.mesh.position.set((x + 0.5) * s, (y + 0.5) * s, (z + 0.5) * s);

    if (stage < 0) {
      entry.mesh.visible = false;
      entry.stage = -1;
      return;
    }

    entry.mesh.visible = true;
    if (stage !== entry.stage) {
      entry.stage = stage;
      entry.material.map = this.crackTextures[Math.min(stage, CRACK_STAGE_COUNT - 1)]!;
      entry.material.needsUpdate = true;
    }
  }

  private validateDamageEntries(): void {
    for (const [key, entry] of this.damage) {
      const [xs, ys, zs] = key.split(',');
      const x = Number(xs);
      const y = Number(ys);
      const z = Number(zs);
      const type = this.world.getBlock(x, y, z);
      if (type !== entry.blockType || !isMineable(type)) {
        this.destroyEntry(key);
      }
    }
  }

  private destroyEntry(key: string): void {
    const entry = this.damage.get(key);
    if (!entry) return;
    entry.mesh.removeFromParent();
    entry.material.dispose();
    this.damage.delete(key);
  }

  private evictOldestIfNeeded(): void {
    if (this.damage.size <= MAX_CRACK_MESHES) return;
    // Drop the least-damaged entry that isn't the current target.
    let worstKey: string | null = null;
    let worstProgress = Infinity;
    for (const [key, entry] of this.damage) {
      if (
        this.current &&
        key === KEY(this.current.x, this.current.y, this.current.z)
      ) {
        continue;
      }
      if (entry.progress < worstProgress) {
        worstProgress = entry.progress;
        worstKey = key;
      }
    }
    if (worstKey) this.destroyEntry(worstKey);
  }
}

function mineSpeedOf(def: ItemDef): number {
  if (def.mineSpeed !== undefined && def.mineSpeed > 0) return def.mineSpeed;
  return Math.max(0.5, def.minePower * 0.85);
}
